/**
 * OrderBadge — Click the position number to reorder a list item.
 *
 * Click "#4" → an input appears prefilled with 4 → type 2 → press Enter/blur →
 * the item moves to position 2 and the rest shift down.
 * Also shows ▲▼ chevrons that move the item one position up/down with a click,
 * to make the reorder feature obvious to first-time users.
 */
import { useState, useRef, useEffect } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

export default function OrderBadge({ index, total, onReorder, variant = "table", testIdPrefix = "order" }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(index + 1));
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (!editing) setValue(String(index + 1));
  }, [index, editing]);

  const commit = () => {
    const n = parseInt(value, 10);
    setEditing(false);
    if (!Number.isFinite(n) || n < 1 || n > total) return;
    const to = n - 1;
    if (to === index) return;
    onReorder(index, to);
  };

  const baseTestId = `${testIdPrefix}-badge-${index}`;

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={1}
        max={total}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setEditing(false); setValue(String(index + 1)); }
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid={`${baseTestId}-input`}
        className={
          variant === "table"
            ? "w-12 text-center border border-teal-400 rounded px-1 py-0.5 text-sm font-semibold bg-white"
            : "w-14 border border-teal-400 rounded px-1.5 py-0.5 text-sm font-bold text-green-700 bg-white"
        }
      />
    );
  }

  const handleClick = (e) => { e.stopPropagation(); setEditing(true); };
  const handleMoveUp = (e) => {
    e.stopPropagation();
    if (index > 0) onReorder(index, index - 1);
  };
  const handleMoveDown = (e) => {
    e.stopPropagation();
    if (index < total - 1) onReorder(index, index + 1);
  };
  const titleAttr = `Click pe nr. pentru a muta lucrarea (tastezi poziția dorită 1–${total}) sau folosește săgețile ▲▼`;

  if (variant === "table") {
    return (
      <div className="inline-flex items-center justify-center gap-0.5" data-testid={`${baseTestId}-wrap`}>
        <div className="flex flex-col">
          <button
            type="button"
            onClick={handleMoveUp}
            disabled={index === 0}
            title="Mută în sus"
            data-testid={`${baseTestId}-up`}
            className="text-slate-400 hover:text-teal-600 disabled:opacity-30 disabled:hover:text-slate-400 leading-none px-0.5"
          >
            <ChevronUp className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={handleMoveDown}
            disabled={index === total - 1}
            title="Mută în jos"
            data-testid={`${baseTestId}-down`}
            className="text-slate-400 hover:text-teal-600 disabled:opacity-30 disabled:hover:text-slate-400 leading-none px-0.5"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
        </div>
        <button
          type="button"
          onClick={handleClick}
          title={titleAttr}
          data-testid={baseTestId}
          className="min-w-[2rem] inline-flex items-center justify-center rounded hover:bg-teal-50 hover:text-teal-700 hover:ring-1 hover:ring-teal-300 font-medium transition-colors cursor-pointer px-1"
        >
          {index + 1}
        </button>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1" data-testid={`${baseTestId}-wrap`}>
      <div className="flex flex-col">
        <button
          type="button"
          onClick={handleMoveUp}
          disabled={index === 0}
          title="Mută în sus"
          data-testid={`${baseTestId}-up`}
          className="text-green-600 hover:text-green-800 disabled:opacity-30 leading-none"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={handleMoveDown}
          disabled={index === total - 1}
          title="Mută în jos"
          data-testid={`${baseTestId}-down`}
          className="text-green-600 hover:text-green-800 disabled:opacity-30 leading-none"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      <button
        type="button"
        onClick={handleClick}
        title={titleAttr}
        data-testid={baseTestId}
        className="font-bold text-green-700 px-2 py-0.5 rounded hover:bg-green-100 hover:ring-1 hover:ring-green-300 transition-colors cursor-pointer"
      >
        #{index + 1}
      </button>
    </div>
  );
}

/** Helper to immutably move an array element from one index to another. */
export const moveItem = (arr, from, to) => {
  if (from === to) return arr;
  const next = arr.slice();
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
};
